import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';

export type SearchEnginesTab = 'listing' | 'answers';

export interface SearchEnginesCardProps {
  /** Beside the title: a Shared or This store chip. */
  badge?: ReactNode;
  /** At the far end of the title row, what the closed card has to say; a null says nothing. */
  meta: ReadonlyArray<string | null>;
  /** The Search listing tab's fields. */
  listing: ReactNode;
  /** The search result preview, beside the listing fields when the card is wide enough. */
  preview: ReactNode;
  /** The Summary and FAQ tab. */
  answers: ReactNode;
  /** Beside the Summary and FAQ tab's name: "4 suggested". */
  answersBadge?: ReactNode;
  /** The tab holding a field error: the card opens on it, so nothing invalid stays hidden. */
  errorTab?: SearchEnginesTab | undefined;
}

/**
 * The Search and answer engines card of both product pages: closed until the user opens it, then
 * the listing beside its preview on one tab and the summary and FAQ on the other. The host supplies
 * the fields, because each page binds them to a form of its own.
 */
export function SearchEnginesCard({
  badge,
  meta,
  listing,
  preview,
  answers,
  answersBadge,
  errorTab,
}: SearchEnginesCardProps) {
  const { t } = useTranslation('products');
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<string>('listing');
  const said = meta.filter(Boolean);
  const [seenError, setSeenError] = useState(errorTab);
  if (errorTab !== seenError) {
    setSeenError(errorTab);
    if (errorTab) {
      setOpen(true);
      setTab(errorTab);
    }
  }

  return (
    <SummaryCard
      title={t('searchCard.title')}
      badge={badge}
      meta={said.length > 0 ? `· ${said.join(' · ')}` : null}
      collapsible
      open={open}
      onOpenChange={setOpen}
    >
      <p className="text-muted-foreground text-[13px]">{t('searchCard.description')}</p>
      <Tabs value={tab} onValueChange={setTab} className="gap-4">
        <TabsList variant="line" className="w-full justify-start">
          <TabsTrigger value="listing" className="flex-none">
            {t('searchCard.tabListing')}
          </TabsTrigger>
          <TabsTrigger value="answers" className="flex-none">
            {t('searchCard.tabAnswers')}
            {answersBadge}
          </TabsTrigger>
        </TabsList>
        {/* Both stay mounted: a tab must not drop what its fields hold. */}
        <TabsContent value="listing" forceMount className="data-[state=inactive]:hidden">
          <div className="@container">
            <div className="grid grid-cols-1 items-start gap-6 @2xl:grid-cols-[minmax(0,1fr)_280px]">
              <div className="flex min-w-0 flex-col gap-4">{listing}</div>
              {preview}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="answers" forceMount className="data-[state=inactive]:hidden">
          {answers}
        </TabsContent>
      </Tabs>
    </SummaryCard>
  );
}
